import { useTranslation } from 'react-i18next';
import { winnersApi, getBadgeName, getCampaignName } from '@/api/recognition';
import type { AdminBadgeDto, CampaignDto } from '@/api/recognition';
import { asset } from '@/utils/basePath';

interface ColumnInfo {
  nameKey: string;
  required: boolean;
  descKey: string;
}

/** Las tres columnas del modo "vigente" (WinnerImportFormat.Columns, mismo orden). Año/OU/Site ya
 *  no se piden: el año sale de la campaña elegida arriba, y OU/Site siempre vinieron del directorio. */
const COLUMNS_VIGENTE: ColumnInfo[] = [
  { nameKey: 'bulk.colNameNombre', required: false, descKey: 'admin.winners.importWizard.colNombreDescVigente' },
  { nameKey: 'bulk.colNameCorreoNomina', required: true, descKey: 'admin.winners.importWizard.colEmailDesc' },
  { nameKey: 'bulk.colNameFundamental', required: false, descKey: 'admin.winners.importWizard.colFundamentalDesc' },
];

/** Las dos columnas del modo "histórica" (WinnerHistoricalImportFormat.Columns, mismo orden). */
const COLUMNS_HISTORICA: ColumnInfo[] = [
  { nameKey: 'bulk.colNameNombre', required: true, descKey: 'admin.winners.importWizard.colNombreDesc' },
  { nameKey: 'bulk.colNameCorreoNomina', required: true, descKey: 'admin.winners.importWizard.colEmailDesc' },
];

interface Props {
  modo: 'vigente' | 'historica';
  onModoChange: (modo: 'vigente' | 'historica') => void;
  campaignId: string;
  /** Catálogo completo, para elegir la campaña AQUÍ mismo — antes solo vivía en la barra de
   *  herramientas y quedaba fuera de vista al abrir el asistente. Solo se pinta en modo "vigente". */
  campaigns: CampaignDto[];
  onCampaignChange: (campaignId: string) => void;
  badgeId: string;
  onBadgeIdChange: (badgeId: string) => void;
  campaignBadges: AdminBadgeDto[];
  onNext: () => void;
  onClose: () => void;
}

/**
 * Paso 1 del asistente de Ganadores: elegir modo (vigente/histórico); campaña (solo vigente,
 * mismo selector que la barra de herramientas); insignia (AMBOS modos, obligatoria — ajuste de UX:
 * antes solo se pedía en histórica y un ganador vigente nacía sin insignia, asignable únicamente
 * editándolo fila por fila después de cargado) con vista previa de la insignia elegida; tabla de
 * columnas requeridas/opcionales; y la descarga de la plantilla real del backend.
 */
export function StepInstructions({
  modo, onModoChange, campaignId, campaigns, onCampaignChange,
  badgeId, onBadgeIdChange, campaignBadges, onNext, onClose,
}: Readonly<Props>) {
  const { t, i18n } = useTranslation('recognition');
  const columnas = modo === 'historica' ? COLUMNS_HISTORICA : COLUMNS_VIGENTE;
  const puedeAvanzar = (modo === 'vigente' ? Boolean(campaignId) : true) && Boolean(badgeId);
  const insigniaElegida = campaignBadges.find((b) => b.id === badgeId) ?? null;

  const descargarPlantilla = async () => {
    try {
      const libro = await winnersApi.getImportTemplate(modo, i18n.language);
      const url = URL.createObjectURL(libro);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ganadores-plantilla.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // El paso 2 (subir archivo) igual valida contra el backend real; un fallo aquí no bloquea el
      // resto del asistente, solo la descarga de ayuda.
    }
  };

  return (
    <div className="bulk-step bulk-step--instructions">
      <div className="adm-toggle-card">
        <div className="adm-toggle-card__info">
          <div>
            <strong>{t('admin.winners.uploadMode')}</strong>
            <p>{t('admin.winners.uploadModalToggleHint')}</p>
          </div>
        </div>
        <label className="switch" title={t('admin.winners.uploadModalToggleHint')}>
          <input
            type="checkbox"
            checked={modo === 'historica'}
            aria-label={t('admin.winners.uploadMode')}
            onChange={(e) => onModoChange(e.target.checked ? 'historica' : 'vigente')}
          />
          <span className="slider" />
        </label>
        <span>{modo === 'historica' ? t('admin.winners.uploadModeHistoric') : t('admin.winners.uploadModeCurrent')}</span>
      </div>

      {modo === 'vigente' && (
        <>
          <label className="adm-field">
            <span>{t('admin.winners.uploadCampaign')} *</span>
            <select
              className="field"
              value={campaignId}
              onChange={(e) => onCampaignChange(e.target.value)}
            >
              <option value="">{t('admin.winners.uploadCampaignPick')}</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{getCampaignName(c, i18n.language)}</option>
              ))}
            </select>
          </label>
          {!campaignId && (
            <p className="adm-note adm-note--warn" role="alert">
              <i className="fas fa-triangle-exclamation" aria-hidden /> {t('admin.winners.pickCampaignFirst')}
            </p>
          )}
        </>
      )}

      {modo === 'historica' && (
        <p className="adm-warn" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden /> {t('admin.winners.uploadHistoricWarning')}
        </p>
      )}

      {/* La insignia es obligatoria en LOS DOS modos (ajuste de UX): elegirla aquí, con vista
          previa, evita subir un padrón entero con la insignia equivocada por no poder verla antes. */}
      <label className="adm-field">
        <span>{t('admin.winners.uploadBadge')} *</span>
        <select
          className="field"
          title={t('admin.winners.uploadBadgeHint')}
          value={badgeId}
          onChange={(e) => onBadgeIdChange(e.target.value)}
        >
          <option value="">{t('admin.winners.uploadBadgePick')}</option>
          {campaignBadges.map((b) => (
            <option key={b.id} value={b.id}>{getBadgeName(b, i18n.language)}</option>
          ))}
        </select>
      </label>
      {insigniaElegida ? (
        <div className="bulk-badge-preview">
          <img src={insigniaElegida.iconUrl || asset('/color-icon.png')} alt="" className="bulk-badge-preview-thumb" />
          <div className="bulk-badge-preview-text">
            <strong>{getBadgeName(insigniaElegida, i18n.language)}</strong>
            <p>{t('admin.winners.importWizard.badgePreviewHint')}</p>
          </div>
        </div>
      ) : (
        <p className="adm-note adm-note--warn" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden /> {t('admin.winners.pickBadgeFirst')}
        </p>
      )}
      <p className="adm-note adm-note--info">
        <i className="fas fa-circle-info" aria-hidden /> {t('admin.winners.uploadBadgeClarify')}
      </p>

      <table className="bulk-columns-table">
        <tbody>
          {columnas.map((column) => (
            <tr key={column.nameKey}>
              <td className="bulk-col-name">{t(column.nameKey)}</td>
              <td>
                <span className={`bulk-col-tag ${column.required ? 'bulk-col-tag--req' : ''}`}>
                  {column.required ? t('bulk.colRequired') : t('bulk.colOptional')}
                </span>
              </td>
              <td className="bulk-col-desc">{t(column.descKey)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="bulk-actions">
        <button type="button" className="btn btn--secondary" onClick={() => void descargarPlantilla()}>
          <i className="fas fa-file-excel" aria-hidden />{' '}
          {modo === 'historica'
            ? t('admin.winners.downloadTemplateHistoric')
            : t('admin.winners.downloadTemplateCurrent')}
        </button>
      </div>

      <div className="bulk-wizard-nav">
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          {t('common.close')}
        </button>
        <button type="button" className="btn btn--send" disabled={!puedeAvanzar} onClick={onNext}>
          {t('bulk.next')} <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );
}

export default StepInstructions;
